import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import copy from 'copy-to-clipboard';
import origin from '~/glm/utils/url';
import GdsUiState from '~/glm/models/gds/gds-ui-state';
import UserStore from '~/glm/models/user-store';
import AnalyticsStore from '~/glm/models/analytics-store';
import EventCollection from '~/glm/models/gds/event-collection';
import HouseholdRow from '~/glm/components/guest-list/household-row.jsx';
import DisabledHouseholdRow from '~/glm/components/guest-list/disabled-household-row.jsx';
import OverviewHouseholdRow from '~/glm/components/guest-list/overview-household-row';
import DisabledOverviewHouseholdRow from '~/glm/components/guest-list/disabled-overview-household-row.jsx';
import GdsHouseholdCollection from '~/glm/models/gds/gds-household-collection';
import NoMatchMessage from '~/glm/components/guest-list/no-match-message';

@observer
class HouseholdList extends Component {
  state = {
    grouping: 'household',
  };

  componentDidMount() {
    const { analyticsStore, gdsHouseholds } = this.props;
    analyticsStore.track('Viewed Guest List', {
      guest_count: gdsHouseholds.guestsCount || 0,
      household_count: gdsHouseholds.size || 0,
    });
  }

  setFilterResultsLength(filterResults) {
    const { gdsUiState } = this.props;

    if (gdsUiState.filterResultsLength !== filterResults) {
      gdsUiState.setFilterResultsLength(filterResults);
    }
  }

  sortFunction = (a, b) => {
    const { gdsUiState } = this.props;
    const direction = gdsUiState.sort.direction;
    let first = a;

    if (a.members.length > b.members.length) first = b;

    if (direction === 'ascending') {
      return first === a ? -1 : 1;
    } else if (direction === 'descending') {
      return first === a ? 1 : -1;
    }
  };

  rackCopyFormClick = formLink => {
    return this.props.analyticsStore.track('Dashboard Interaction', {
      action: 'copy form link clicked',
      source: 'guest list',
      formLink,
    });
  };

  trackCopyFormClick = formLink => {
    return this.props.analyticsStore.track('Dashboard Interaction', {
      action: 'copy form link clicked',
      source: 'guest list',
      formLink,
    });
  };

  handleCopyFormLink = (name, householdId) => {
    const { snackbarRef } = this.props.gdsUiState;
    const { wedding } = this.props.userStore;

    // guest info request form link and copy to clipboard
    const formLink = `${origin()}/ac/${wedding.vanityUrl || wedding.slug}/${householdId}`;
    copy(formLink);

    this.trackCopyFormClick(formLink);

    if (snackbarRef) {
      snackbarRef.current.showAndDismiss({
        data: {
          type: 'COPY_FORM_LINK',
          name,
          formLink,
        },
      });
    }
  };

  render() {
    const {
      userStore,
      gdsUiState,
      gdsEvents,
      editHousehold,
      trackInviteSend,
      trackMeal,
      trackThankYou,
      gdsHouseholds,
      analyticsStore,
      usesQuestions,
    } = this.props;
    const { selectedEvent, sort, addressFilter } = gdsUiState;

    if (selectedEvent) {
      let households = [...selectedEvent.invitedHouseholds];

      if (addressFilter) {
        households = [...selectedEvent.getFilteredAndInvitedHouseholds(addressFilter)];
        this.setFilterResultsLength(households.length);
      }
      if (sort.direction && sort.attribute === 'partyOf') {
        households = households.sort(this.sortFunction);
      }

      if (households.length) {
        return households.map(household => (
          <HouseholdRow
            userStore={userStore}
            event={selectedEvent}
            household={household}
            gdsUiState={gdsUiState}
            key={household.id}
            rsvpEnabled={gdsEvents.rsvpEnabled}
            editHousehold={editHousehold}
            analyticsStore={analyticsStore}
            onCopyFormLinkClick={this.handleCopyFormLink}
            trackInviteSend={trackInviteSend}
            trackMeal={trackMeal}
            trackThankYou={trackThankYou}
            usesQuestions={usesQuestions}
          />
        ));
      } else {
        return addressFilter ? (
          <NoMatchMessage />
        ) : (
          <DisabledHouseholdRow
            event={selectedEvent}
            gdsUiState={gdsUiState}
            editHousehold={editHousehold}
            trackInviteSend={trackInviteSend}
            trackMeal={trackMeal}
            trackThankYou={trackThankYou}
            usesQuestions={usesQuestions}
          />
        );
      }
    } else {
      if (gdsHouseholds.size) {
        let households = gdsHouseholds;

        if (addressFilter) {
          households = gdsUiState.filteredHouseholds;
          this.setFilterResultsLength(households.length);
        }

        if (sort.direction && sort.attribute === 'partyOf') {
          households = households.sort(this.sortFunction);
        }

        return households.map(household => (
          <OverviewHouseholdRow
            household={household}
            gdsUiState={gdsUiState}
            gdsEvents={gdsEvents}
            key={household.id}
            editHousehold={editHousehold}
            analyticsStore={analyticsStore}
            onCopyFormLinkClick={this.handleCopyFormLink}
            trackInviteSend={trackInviteSend}
            trackMeal={trackMeal}
            trackThankYou={trackThankYou}
          />
        ));
      } else {
        return (
          <DisabledOverviewHouseholdRow
            gdsUiState={gdsUiState}
            gdsEvents={gdsEvents}
            editHousehold={editHousehold}
            trackInviteSend={trackInviteSend}
            trackMeal={trackMeal}
            trackThankYou={trackThankYou}
          />
        );
      }
    }
  }
}

HouseholdList.propTypes = {
  userStore: PropTypes.instanceOf(UserStore).isRequired,
  gdsUiState: PropTypes.instanceOf(GdsUiState).isRequired,
  gdsHouseholds: PropTypes.instanceOf(GdsHouseholdCollection),
  gdsEvents: PropTypes.instanceOf(EventCollection),
  analyticsStore: PropTypes.instanceOf(AnalyticsStore).isRequired,
  editHousehold: PropTypes.func.isRequired,
  trackInviteSend: PropTypes.func.isRequired,
  trackThankYou: PropTypes.func.isRequired,
  trackMeal: PropTypes.func.isRequired,
  usesQuestions: PropTypes.bool.isRequired,
};

export default HouseholdList;
